import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["New", "Contacted", "Quotation Sent", "Negotiating", "Confirmed", "Completed", "Cancelled"];
const STATUS_COLORS = {
  New: "text-champagne border-champagne/50",
  Contacted: "text-ivory border-white/30",
  "Quotation Sent": "text-blue-300 border-blue-300/40",
  Negotiating: "text-amber-300 border-amber-300/40",
  Confirmed: "text-emerald-300 border-emerald-300/40",
  Completed: "text-emerald-400 border-emerald-400/40",
  Cancelled: "text-red-400 border-red-400/40",
};

const EMPTY_PRODUCT = {
  name: "", brand: "CELLO", category: "Luxury Glassware", short_description: "", description: "",
  image: "", moq: "", price_range: "Price on Request", colors: "", specs: "", packaging: "",
  lead_time: "", custom_branding: false, featured: false, seasonal: false, occasions: "",
};

const inputCls = "w-full bg-transparent border border-white/15 focus:border-champagne text-ivory text-sm px-3 py-2.5 outline-none transition-colors";
const labelCls = "block text-[10px] font-mono uppercase tracking-[0.2em] text-ivory/50 mb-1.5";

function ProductForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      colors: form.colors ? form.colors.split(",").map((s) => s.trim()).filter(Boolean) : [],
      specs: form.specs ? form.specs.split(",").map((s) => s.trim()).filter(Boolean) : [],
      occasions: form.occasions ? form.occasions.split(",").map((s) => s.trim()).filter(Boolean) : [],
    };
    try {
      await onSave(payload);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="product-form-modal" className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4">
      <form onSubmit={submit} className="w-full max-w-2xl bg-[#161616] border border-white/10 p-6 sm:p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-2xl text-ivory">{initial.id ? "Edit Product" : "Add Product"}</h2>
          <button type="button" data-testid="product-form-close" onClick={onClose} className="text-ivory/50 hover:text-ivory"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={labelCls}>Name *</label><input data-testid="pf-name" required className={inputCls} value={form.name} onChange={set("name")} /></div>
          <div><label className={labelCls}>Brand *</label>
            <select data-testid="pf-brand" className={inputCls} value={form.brand} onChange={set("brand")}>
              {["CELLO", "VENUS", "AGARO", "THE GOOD GRID"].map((b) => <option key={b} className="bg-charcoal">{b}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Category *</label>
            <select data-testid="pf-category" className={inputCls} value={form.category} onChange={set("category")}>
              {["Luxury Glassware", "Premium Cutlery", "Kitchen Appliances", "Cookware", "Corporate Gift Sets", "Executive Gifts", "Festive Gifting", "Employee Gifting"].map((c) => <option key={c} className="bg-charcoal">{c}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>Image URL</label><input data-testid="pf-image" className={inputCls} value={form.image} onChange={set("image")} placeholder="https://…" /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Short Description</label><input data-testid="pf-short-desc" className={inputCls} value={form.short_description} onChange={set("short_description")} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Description</label><textarea data-testid="pf-description" rows={3} className={inputCls} value={form.description} onChange={set("description")} /></div>
          <div><label className={labelCls}>MOQ</label><input data-testid="pf-moq" className={inputCls} value={form.moq} onChange={set("moq")} placeholder="e.g. 50 units" /></div>
          <div><label className={labelCls}>Price Range</label><input data-testid="pf-price-range" className={inputCls} value={form.price_range} onChange={set("price_range")} /></div>
          <div><label className={labelCls}>Colours (comma separated)</label><input data-testid="pf-colors" className={inputCls} value={form.colors} onChange={set("colors")} /></div>
          <div><label className={labelCls}>Lead Time</label><input data-testid="pf-lead-time" className={inputCls} value={form.lead_time} onChange={set("lead_time")} /></div>
          <div className="sm:col-span-2"><label className={labelCls}>Specifications (comma separated)</label><input data-testid="pf-specs" className={inputCls} value={form.specs} onChange={set("specs")} /></div>
          <div><label className={labelCls}>Packaging</label><input data-testid="pf-packaging" className={inputCls} value={form.packaging} onChange={set("packaging")} /></div>
          <div><label className={labelCls}>Occasions (comma separated)</label><input data-testid="pf-occasions" className={inputCls} value={form.occasions} onChange={set("occasions")} /></div>
          <label className="flex items-center gap-2 text-sm text-ivory/70"><input data-testid="pf-custom-branding" type="checkbox" checked={form.custom_branding} onChange={set("custom_branding")} className="accent-[#C5A059]" /> Custom branding</label>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-ivory/70"><input data-testid="pf-featured" type="checkbox" checked={form.featured} onChange={set("featured")} className="accent-[#C5A059]" /> Featured</label>
            <label className="flex items-center gap-2 text-sm text-ivory/70"><input data-testid="pf-seasonal" type="checkbox" checked={form.seasonal} onChange={set("seasonal")} className="accent-[#C5A059]" /> Seasonal</label>
          </div>
        </div>
        <button data-testid="pf-save" type="submit" disabled={saving} className="mt-8 w-full bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.22em] py-4 hover:bg-champagne-light transition-colors disabled:opacity-60">
          {saving ? "Saving…" : "Save Product"}
        </button>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [authed, setAuthed] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/admin/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = "Dashboard — THE GOOD GRID";
    api.get("/auth/me")
      .then(() => { setAuthed(true); load(); })
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate, load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      toast.success("Status updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const [sendingQuote, setSendingQuote] = useState(null);
  const sendQuote = async (id) => {
    setSendingQuote(id);
    try {
      await api.post(`/admin/enquiries/${id}/send-quote`);
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status: "Quotation Sent" } : e)));
      toast.success("Quotation email sent to the lead");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingQuote(null);
    }
  };

  const saveProduct = async (payload) => {
    if (editing.id) {
      await api.put(`/admin/products/${editing.id}`, payload);
      toast.success("Product updated");
    } else {
      await api.post("/admin/products", payload);
      toast.success("Product added");
    }
    setEditing(null);
    load();
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  if (!authed) return <div className="bg-charcoal min-h-screen" />;

  const newCount = enquiries.filter((e) => e.status === "New").length;

  return (
    <div data-testid="admin-dashboard" className="bg-charcoal text-ivory min-h-screen pt-24 px-4 sm:px-8 lg:px-16 pb-24">
      <div className="flex flex-wrap justify-between items-center gap-4 mb-10">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-champagne">CMS</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-2">Dashboard</h1>
        </div>
        <button
          data-testid="admin-logout"
          onClick={() => { localStorage.removeItem("tgg_admin_token"); navigate("/admin/login"); }}
          className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-ivory/50 hover:text-ivory border border-white/15 px-4 py-2.5 transition-colors"
        >
          <LogOut size={13} /> Sign Out
        </button>
      </div>

      <div className="flex gap-2 mb-8 border-b border-white/10 pb-4">
        <button data-testid="tab-enquiries" onClick={() => setTab("enquiries")} className={`text-[11px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${tab === "enquiries" ? "border-champagne text-champagne" : "border-white/10 text-ivory/50"}`}>
          Enquiries ({enquiries.length}){newCount > 0 && <span className="ml-2 bg-champagne text-charcoal px-1.5 py-0.5 text-[10px]">{newCount} new</span>}
        </button>
        <button data-testid="tab-products" onClick={() => setTab("products")} className={`text-[11px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 border transition-colors ${tab === "products" ? "border-champagne text-champagne" : "border-white/10 text-ivory/50"}`}>
          Products ({products.length})
        </button>
      </div>

      {tab === "enquiries" && (
        <div data-testid="enquiries-panel" className="space-y-4">
          {enquiries.length === 0 && <p className="text-ivory/40 font-serif text-xl">No enquiries yet.</p>}
          {enquiries.map((e) => (
            <div key={e.id} data-testid={`enquiry-row-${e.id}`} className="border border-white/10 bg-[#161616] p-6">
              <div className="flex flex-wrap justify-between gap-4 items-start">
                <div>
                  <p className="font-serif text-xl">{e.full_name} {e.company && <span className="text-ivory/40 text-base">— {e.company}</span>}</p>
                  <p className="text-xs text-ivory/45 mt-1">{e.email} {e.phone && `· ${e.phone}`} {e.designation && `· ${e.designation}`}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ivory/30 mt-2">{new Date(e.created_at).toLocaleString()}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <select
                    data-testid={`enquiry-status-${e.id}`}
                    value={e.status}
                    onChange={(ev) => setStatus(e.id, ev.target.value)}
                    className={`bg-transparent border text-[10px] font-mono uppercase tracking-[0.15em] px-3 py-2 outline-none ${STATUS_COLORS[e.status] || "text-ivory border-white/20"}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-charcoal text-ivory">{s}</option>)}
                  </select>
                  <button
                    data-testid={`enquiry-send-quote-${e.id}`}
                    onClick={() => sendQuote(e.id)}
                    disabled={sendingQuote === e.id}
                    className="text-[10px] font-mono uppercase tracking-[0.15em] border border-champagne/50 text-champagne px-3 py-2 hover:bg-champagne hover:text-charcoal transition-colors disabled:opacity-50"
                  >
                    {sendingQuote === e.id ? "Sending…" : "Email Quote"}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 text-xs">
                {e.quantity && <div><p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-1">Quantity</p><p className="text-ivory/70">{e.quantity}</p></div>}
                {e.budget_per_gift && <div><p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-1">Budget / gift</p><p className="text-ivory/70">{e.budget_per_gift}</p></div>}
                {e.occasion && <div><p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-1">Occasion</p><p className="text-ivory/70">{e.occasion}</p></div>}
                {e.cities && <div><p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-1">Delivery</p><p className="text-ivory/70">{e.cities}{e.delivery_date && ` · ${e.delivery_date}`}</p></div>}
                {e.category && <div><p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-1">Category</p><p className="text-ivory/70">{e.category}</p></div>}
                {e.brand && <div><p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-1">Brand</p><p className="text-ivory/70">{e.brand}</p></div>}
                <div><p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-1">Branding</p><p className="text-ivory/70">{e.custom_branding ? "Yes" : "No"}</p></div>
                {e.packaging && <div><p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-1">Packaging</p><p className="text-ivory/70">{e.packaging}</p></div>}
              </div>
              {e.products?.length > 0 && (
                <div className="mt-4 border-t border-white/10 pt-4">
                  <p className="text-ivory/35 uppercase font-mono text-[9px] tracking-[0.2em] mb-2">Requested products</p>
                  <ul className="text-xs text-ivory/70 space-y-1">
                    {e.products.map((p, i) => <li key={i}>{p.name}{p.quantity > 0 && ` — ${p.quantity} units`}</li>)}
                  </ul>
                </div>
              )}
              {e.notes && <p className="mt-4 text-xs text-ivory/50 border-t border-white/10 pt-4">{e.notes}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === "products" && (
        <div data-testid="products-panel">
          <button
            data-testid="add-product-btn"
            onClick={() => setEditing({ ...EMPTY_PRODUCT })}
            className="flex items-center gap-2 bg-champagne text-charcoal text-[11px] font-mono uppercase tracking-[0.2em] px-5 py-3 hover:bg-champagne-light transition-colors mb-6"
          >
            <Plus size={14} /> Add Product
          </button>
          <div className="border border-white/10 divide-y divide-white/10">
            {products.map((p) => (
              <div key={p.id} data-testid={`admin-product-${p.id}`} className="flex items-center gap-4 p-4 bg-[#161616]">
                <div className="w-12 h-14 shrink-0 bg-charcoal-soft overflow-hidden">
                  {p.image && <img src={p.image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-serif truncate">{p.name}</p>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-ivory/40">{p.brand} · {p.category}{p.featured && " · Featured"}{p.seasonal && " · Seasonal"}</p>
                </div>
                <button data-testid={`admin-edit-${p.id}`} onClick={() => setEditing({
                  ...p,
                  colors: (p.colors || []).join(", "),
                  specs: (p.specs || []).join(", "),
                  occasions: (p.occasions || []).join(", "),
                })} className="text-ivory/50 hover:text-champagne p-2 transition-colors"><Pencil size={15} /></button>
                <button data-testid={`admin-delete-${p.id}`} onClick={() => deleteProduct(p.id)} className="text-ivory/50 hover:text-red-400 p-2 transition-colors"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && <ProductForm initial={editing} onSave={saveProduct} onClose={() => setEditing(null)} />}
    </div>
  );
}
